import { useCallback, useState } from "react";

import { FrameStencilsApi } from "../../hooks";
import { FrameStencils, MarkerText, MarkerType, StencilType } from "../../types";
import { sliceItemUpdate } from "../../utils";

export interface UseTextMarkerEditorProps {
  frameStencils: FrameStencils;
  updateFrameStencils: FrameStencilsApi["updateFrameStencils"];
}

export interface TextMarkerEditor {
  isTextEditorOpen: boolean;
  textMarkerEditing: MarkerText | null;
  setTextMarkerText: (text: string) => void;
  openTextEditor: (id: string) => void;
  onSubmitTextEdit: () => void;
  cancelTextEdit: () => void;
}

export const useTextMarkerEditor = ({
  frameStencils,
  updateFrameStencils,
}: UseTextMarkerEditorProps): TextMarkerEditor => {
  const [isTextEditorOpen, setIsTextEditorOpen] = useState<boolean>(false);
  const [textMarkerEditing, setTextMarkerEditing] = useState<MarkerText | null>(null);
  const [textMarkerText, setTextMarkerText] = useState<string>("");

  const openTextEditor = useCallback(
    (id: string) => {
      const textMarkerToEdit = frameStencils.find(
        (stencil): stencil is MarkerText =>
          stencil.stencilType === StencilType.MARKER && stencil.markerType === MarkerType.TEXT && stencil.id === id
      );
      if (textMarkerToEdit) {
        setTextMarkerEditing(textMarkerToEdit);
        setIsTextEditorOpen(true);
      }
    },
    [frameStencils]
  );

  const onSubmitTextEdit = useCallback(() => {
    if (textMarkerEditing && textMarkerText) {
      updateFrameStencils((prevState) => {
        if (!prevState) return prevState;
        const newTextMarker: MarkerText = { ...textMarkerEditing, label: textMarkerText };
        return sliceItemUpdate({ item: newTextMarker, arr: prevState });
      });
    }
    setIsTextEditorOpen(false);
  }, [textMarkerEditing, textMarkerText, updateFrameStencils]);

  const cancelTextEdit = useCallback(() => {
    setIsTextEditorOpen(false);
    setTextMarkerEditing(null);
    setTextMarkerText("");
  }, []);

  return { isTextEditorOpen, textMarkerEditing, setTextMarkerText, openTextEditor, onSubmitTextEdit, cancelTextEdit };
};
